import React, { useCallback, useEffect, useState } from 'react';
import './AdminPage.css';
import AppHeader from './AppHeader';
import apiService from '../services/api';
import { triggerHaptic } from '../services/telegram';

const PAGE_SIZE = 25;

const PERIODS = [
  { key: 'week', label: '7 дней' },
  { key: 'month', label: '30 дней' },
  { key: '90d', label: '90 дней' },
  { key: '365d', label: '365 дней' },
  { key: 'custom', label: 'Свой' },
];

const KPI_CARDS = [
  {
    key: 'active_paid_vpn_7d',
    label: 'Платящие, которые пользовались VPN',
    hint: 'хотя бы одно подключение за последние 7 дней периода',
    group: 'VPN-активность',
    explanation: 'Пользователи с действующей платной подпиской, которые подключались к VPN хотя бы один раз за последние 7 дней выбранного периода.',
  },
  {
    key: 'revenue_rub',
    label: 'Сумма успешных оплат',
    hint: 'за выбранный период',
    group: 'Оплаты',
    explanation: 'Сумма платежей, которые платёжная система подтвердила как успешно оплаченные.',
  },
  {
    key: 'first_payment_users',
    label: 'Купили подписку впервые',
    hint: 'уникальные пользователи за период',
    group: 'Оплаты',
    explanation: 'Пользователи, у которых в выбранном периоде прошла первая успешная оплата за всё время.',
  },
  {
    key: 'renewal_users',
    label: 'Купили подписку повторно',
    hint: 'уникальные пользователи за период',
    group: 'Оплаты',
    explanation: 'Пользователи, у которых в выбранном периоде прошла повторная успешная оплата.',
  },
  {
    key: 'new_users',
    label: 'Зарегистрировались',
    hint: 'новые пользователи за период',
    group: 'Пользователи',
    explanation: 'Пользователи, которые впервые появились в базе Rocket VPN в выбранном периоде.',
  },
  {
    key: 'first_vpn_users',
    label: 'Впервые подключились к VPN',
    hint: 'уникальные пользователи за период',
    group: 'VPN-активность',
    explanation: 'Пользователи, у которых первое зафиксированное VPN-подключение произошло в выбранном периоде.',
  },
];

const CHART_METRICS = [
  ['vpn_dau', 'VPN-пользователи за день', '#2f81f7'],
  ['new_users', 'Новые регистрации', '#3fb950'],
  ['first_vpn_users', 'Первые VPN-подключения', '#a371f7'],
  ['first_payment_users', 'Первые оплаты', '#ff9500'],
  ['renewal_users', 'Повторные оплаты', '#f85149'],
  ['revenue_rub', 'Оплаты, ₽', '#39c5cf'],
  ['active_paid_subscriptions', 'Оплаченные подписки', '#6e7681'],
  ['active_paid_vpn_7d', 'Платящие с VPN-активностью', '#58a6ff'],
];

const ADMIN_NAV = [
  { k: 'stats', t: 'Обзор' }, { k: 'users', t: 'Пользователи' },
  { k: 'search', t: 'Поиск' }, { k: 'funnels', t: 'Воронки' },
  { k: 'ads', t: 'Метки' }, { k: 'admins', t: 'Доступы' },
];

const SORTS = [
  { key: 'registered_at', label: 'Дата' },
  { key: 'username', label: 'Username' },
  { key: 'first_name', label: 'Имя' },
  { key: 'userid', label: 'ID' },
];

function fmtDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_) {
    return String(value);
  }
}

function shortDay(iso) {
  const parts = String(iso).split('-');
  return parts.length === 3 ? `${parts[2]}.${parts[1]}` : iso;
}

function fmtRub(value) {
  return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(Number(value || 0))} ₽`;
}

function Chart({ points, metrics }) {
  const [hoverIndex, setHoverIndex] = useState(null);
  if (!points || points.length === 0) {
    return <div className="adm-chart-empty">Нет данных за период</div>;
  }
  const W = 920;
  const H = 300;
  const hasRevenue = metrics.includes('revenue_rub');
  const pad = { top: 30, right: hasRevenue ? 76 : 22, bottom: 34, left: 52 };
  const iw = W - pad.left - pad.right;
  const ih = H - pad.top - pad.bottom;
  const leftFields = metrics.filter((m) => m !== 'revenue_rub');
  const maxRaw = Math.max(...points.flatMap((p) => leftFields.map((m) => Number(p[m] || 0))), 0);
  const max = maxRaw > 0 ? maxRaw : 1;
  const revenueMaxRaw = Math.max(...points.map((p) => Number(p.revenue_rub || 0)), 0);
  const revenueMax = revenueMaxRaw > 0 ? revenueMaxRaw : 1;
  const n = points.length;
  const xAt = (i) => pad.left + (n <= 1 ? iw / 2 : (i / (n - 1)) * iw);
  const yAt = (v, field) => pad.top + ih - (v / (field === 'revenue_rub' ? revenueMax : max)) * ih;
  const path = (field) => {
    let drawing = false;
    return points.map((p, i) => {
      const value = p[field];
      if (value == null) { drawing = false; return ''; }
      const command = drawing ? 'L' : 'M';
      drawing = true;
      return `${command}${xAt(i).toFixed(1)},${yAt(value, field).toFixed(1)}`;
    }).filter(Boolean).join(' ');
  };
  const grid = [0, max / 2, max];
  const labels = n <= 1 ? [0] : [0, Math.floor((n - 1) / 2), n - 1];

  return (
    <svg className="adm-chart" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" role="img"
      onMouseLeave={() => setHoverIndex(null)} onMouseMove={(e) => { const b=e.currentTarget.getBoundingClientRect(); const px=(e.clientX-b.left)/b.width*W; setHoverIndex(Math.max(0,Math.min(n-1,Math.round(((px-pad.left)/iw)*(n-1))))); }}>
      <defs>
        <linearGradient id="admVpnFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4aa3ff" stopOpacity="0.30" />
          <stop offset="100%" stopColor="#4aa3ff" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <text x={pad.left} y="14" className="adm-axis-title">ПОЛЬЗОВАТЕЛИ / СОБЫТИЯ</text>
      {hasRevenue && <text x={W - pad.right} y="14" textAnchor="end" className="adm-axis-title">ОПЛАТЫ, ₽</text>}
      {grid.map((g, i) => (
        <g key={i}>
          <line x1={pad.left} y1={yAt(g)} x2={W - pad.right} y2={yAt(g)} className="adm-grid" />
          <text x={pad.left - 6} y={yAt(g) + 4} textAnchor="end" className="adm-axis">
            {Math.round(g)}
          </text>
        </g>
      ))}
      {hasRevenue && [0, revenueMax / 2, revenueMax].map((g, i) => <text key={`r${i}`} x={W - pad.right + 7} y={yAt(g, 'revenue_rub') + 4} className="adm-axis">{Math.round(g)} ₽</text>)}
      {metrics.map((field) => {
        const meta = CHART_METRICS.find((m) => m[0] === field);
        return <path key={field} d={path(field)} className="adm-line" stroke={meta?.[2]} fill="none" />;
      })}
      {points.map((p, i) => metrics.map((field) => p[field] == null ? null : <circle key={`${field}-${i}`} cx={xAt(i)} cy={yAt(p[field], field)} r={2.4} fill={CHART_METRICS.find((m) => m[0] === field)?.[2]}><title>{`${shortDay(p.date)} · ${CHART_METRICS.find((m) => m[0] === field)?.[1]}: ${field === 'revenue_rub' ? fmtRub(p[field]) : p[field]}`}</title></circle>))}
      {hoverIndex != null && <g pointerEvents="none">
        <line x1={xAt(hoverIndex)} x2={xAt(hoverIndex)} y1={pad.top} y2={pad.top+ih} stroke="rgba(255,255,255,.35)" strokeDasharray="3 3" />
        <rect x={Math.min(W-225,Math.max(5,xAt(hoverIndex)+10))} y={22} width="215" height={34+metrics.length*18} rx="8" fill="#251d43" stroke="#50447b" />
        <text x={Math.min(W-215,Math.max(15,xAt(hoverIndex)+20))} y="42" fill="#fff" fontSize="12" fontWeight="700">{points[hoverIndex].date}</text>
        {metrics.map((field,j) => <text key={field} x={Math.min(W-215,Math.max(15,xAt(hoverIndex)+20))} y={62+j*18} fill={CHART_METRICS.find((m)=>m[0]===field)?.[2]} fontSize="11">{CHART_METRICS.find((m)=>m[0]===field)?.[1]}: {field==='revenue_rub'?fmtRub(points[hoverIndex][field]):(points[hoverIndex][field] ?? '—')}</text>)}
      </g>}
      {labels.map((i) => (
        <text key={i} x={xAt(i)} y={H - 7} textAnchor="middle" className="adm-axis">
          {shortDay(points[i].date)}
        </text>
      ))}
    </svg>
  );
}

function AdminPage({ user, onBack }) {
  const [tab, setTab] = useState('stats');
  const [period, setPeriod] = useState('week');
  const [customDays, setCustomDays] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [summary, setSummary] = useState(null);
  const [statsLoadedAt, setStatsLoadedAt] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [compare, setCompare] = useState(true);
  const [chartMetrics, setChartMetrics] = useState(['vpn_dau', 'new_users']);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState('registered_at');
  const [order, setOrder] = useState('desc');
  const [loadingRows, setLoadingRows] = useState(false);
  const [userFilter, setUserFilter] = useState('all');

  const [admins, setAdmins] = useState([]);
  const [newAdminId, setNewAdminId] = useState('');
  const [adminBusy, setAdminBusy] = useState(false);

  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchStatus, setSearchStatus] = useState('all');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [card, setCard] = useState(null);
  const [cardBusy, setCardBusy] = useState(false);

  const [funnelId, setFunnelId] = useState(1);
  const [funnelDays, setFunnelDays] = useState(30);
  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);

  const [ads, setAds] = useState([]);
  const [adsLoading, setAdsLoading] = useState(false);
  const [adCode, setAdCode] = useState('');
  const [serviceBusy, setServiceBusy] = useState(false);

  const loadFunnel = useCallback(async (fid, days) => {
    setFunnelLoading(true);
    setError(null);
    try {
      setFunnel(await apiService.adminFunnels(fid, days));
    } catch (e) {
      setError(e.message || 'Ошибка загрузки воронки');
    }
    setFunnelLoading(false);
  }, []);

  const loadAds = useCallback(async () => {
    setAdsLoading(true);
    setError(null);
    try {
      const res = await apiService.adminAdsList(60);
      setAds(res.items || []);
    } catch (e) {
      setError(e.message || 'Ошибка загрузки меток');
    }
    setAdsLoading(false);
  }, []);

  const createAd = async () => {
    const c = String(adCode || '').trim();
    if (!c) return;
    setServiceBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await apiService.adminAdsCreate(c);
      setAdCode('');
      setNotice(`Метка создана: ${res.link}`);
      await loadAds();
    } catch (e) {
      setError(e.message || 'Не удалось создать метку');
    }
    setServiceBusy(false);
  };

  const copyText = (t) => {
    triggerHaptic();
    try { navigator.clipboard.writeText(t); setNotice('Скопировано'); } catch (_) {}
  };

  const gkReload = async (full) => {
    setServiceBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = full ? await apiService.adminGkReloadAndRefresh() : await apiService.adminGkReloadServers();
      if (full) {
        setNotice(res.started ? 'Обновление ключей Happ запущено в фоне' : (res.running ? 'Обновление уже выполняется' : 'Готово'));
      } else {
        setNotice(`Конфиг перезагружен: панелей ${res.panels_after} (было ${res.panels_before})`);
      }
    } catch (e) {
      setError(e.message || 'GiveKeys недоступен');
    }
    setServiceBusy(false);
  };

  const runSearch = async (status = searchStatus) => {
    setSearching(true);
    setError(null);
    setCard(null);
    try {
      const res = await apiService.adminSearchUsers({ query: searchQuery.trim(), status, limit: 20 });
      setSearchResults(res.items || []);
      if ((res.items || []).length === 0) setNotice('Никого не найдено');
      else setNotice(null);
    } catch (e) {
      setError(e.message || 'Ошибка поиска');
    }
    setSearching(false);
  };

  const openCard = async (uid) => {
    triggerHaptic();
    setCardBusy(true);
    setError(null);
    try {
      setCard(await apiService.adminUserCard(uid));
    } catch (e) {
      setError(e.message || 'Не удалось загрузить карточку');
    }
    setCardBusy(false);
  };

  const doAction = async (fn, okText) => {
    setCardBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(okText);
      if (card?.userid) setCard(await apiService.adminUserCard(card.userid));
    } catch (e) {
      setError(e.message || 'Действие не выполнено');
    }
    setCardBusy(false);
  };

  const rangeParams = useCallback(() => {
    if (period === 'custom') {
      if (dateFrom || dateTo) return { date_from: dateFrom || undefined, date_to: dateTo || undefined };
      const d = parseInt(customDays, 10);
      return { days: Number.isFinite(d) && d > 0 ? d : 30 };
    }
    return { period };
  }, [period, customDays, dateFrom, dateTo]);

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    setError(null);
    try {
      const nextSummary = await apiService.adminStatsTimeseries(rangeParams());
      setSummary(nextSummary);
      setStatsLoadedAt(new Date());
    } catch (e) {
      setError(e.message || 'Ошибка загрузки статистики');
    }
    setLoadingStats(false);
  }, [rangeParams]);

  const loadRows = useCallback(async () => {
    setLoadingRows(true);
    setError(null);
    try {
      const res = await apiService.adminUsersInRange({
        ...rangeParams(),
        status: userFilter,
        sort,
        order,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setRows(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      setError(e.message || 'Ошибка загрузки пользователей');
    }
    setLoadingRows(false);
  }, [rangeParams, userFilter, sort, order, page]);

  const loadAdmins = useCallback(async () => {
    try {
      const res = await apiService.adminListAdmins();
      setAdmins(res.items || []);
    } catch (e) {
      setError(e.message || 'Ошибка загрузки админов');
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { if (tab === 'users') loadRows(); }, [tab, loadRows]);
  useEffect(() => { if (tab === 'admins') loadAdmins(); }, [tab, loadAdmins]);
  useEffect(() => { if (tab === 'funnels') loadFunnel(funnelId, funnelDays); }, [tab, funnelId, funnelDays, loadFunnel]);
  useEffect(() => { if (tab === 'ads') loadAds(); }, [tab, loadAds]);

  const applyPeriod = (key) => {
    triggerHaptic();
    setPeriod(key);
    setPage(0);
  };

  const toggleSort = (key) => {
    triggerHaptic();
    if (sort === key) setOrder((o) => (o === 'desc' ? 'asc' : 'desc'));
    else {
      setSort(key);
      setOrder('desc');
    }
    setPage(0);
  };

  const handleAddAdmin = async () => {
    const id = parseInt(String(newAdminId).trim(), 10);
    if (!Number.isFinite(id) || id <= 0) {
      setError('Введите числовой Telegram ID');
      return;
    }
    setAdminBusy(true);
    setError(null);
    setNotice(null);
    try {
      await apiService.adminAddAdmin(id);
      setNewAdminId('');
      setNotice(`Админ ${id} добавлен`);
      await loadAdmins();
    } catch (e) {
      setError(e.message || 'Не удалось добавить админа');
    }
    setAdminBusy(false);
  };

  const handleRemoveAdmin = async (id) => {
    if (typeof window !== 'undefined' && !window.confirm(`Удалить админа ${id}?`)) return;
    setAdminBusy(true);
    setError(null);
    setNotice(null);
    try {
      await apiService.adminRemoveAdmin(id);
      setNotice(`Админ ${id} удалён`);
      await loadAdmins();
    } catch (e) {
      setError(e.message || 'Не удалось удалить админа');
    }
    setAdminBusy(false);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const myId = user?.userid ?? user?.id;
  const toggleChartMetric = (key) => {
    setChartMetrics((current) => {
      if (current.includes(key)) return current.length > 1 ? current.filter((x) => x !== key) : current;
      return current.length < 3 ? [...current, key] : current;
    });
  };

  const formatKpi = (key, value) => {
    if (value == null) return '—';
    return key === 'revenue_rub' ? fmtRub(value) : new Intl.NumberFormat('ru-RU').format(value);
  };
  const formatDelta = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return '';
    if (number === 0) return 'столько же, сколько в прошлом периоде';
    const amount = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(Math.abs(number));
    return `на ${amount}% ${number > 0 ? 'больше' : 'меньше'}, чем в прошлом периоде`;
  };
  const exportDailyCsv = () => {
    const fields = ['date','new_users','vpn_keys_issued','first_vpn_users','vpn_dau','active_paid_subscriptions','active_paid_vpn_7d','first_payment_users','renewal_users','revenue_rub'];
    const rowsCsv = (summary?.points || []).map((p) => fields.map((f) => p[f] ?? '').join(';'));
    const blob = new Blob([`\uFEFF${fields.join(';')}\n${rowsCsv.join('\n')}`], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'daily_metrics.csv'; a.click(); URL.revokeObjectURL(a.href);
  };
  const exportAuditCsv = async () => {
    try {
      const blob = await apiService.adminExportUserDay(rangeParams());
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'analytics_user_day.csv'; a.click(); URL.revokeObjectURL(a.href);
    } catch (e) { setError(e.message || 'Не удалось выгрузить проверочный CSV'); }
  };

  return (
    <div className="admin-page">
      <div className="background"></div>
      <aside className="adm-sidebar">
        <div className="adm-brand"><span className="adm-brand-mark">R</span><span><b>Rocket VPN</b><small>CONTROL CENTER</small></span></div>
        <nav>{ADMIN_NAV.map((x) => <button key={x.k} type="button" className={tab === x.k ? 'active' : ''} onClick={() => setTab(x.k)}>{x.t}</button>)}</nav>
      </aside>
      <div className="adm-workspace">
      <AppHeader title="Админ-панель" />
      <div className="adm-content">
        {error && <div className="adm-error">{error}</div>}
        {notice && <div className="adm-notice">{notice}</div>}

        {tab === 'search' && (
          <div className="adm-card">
            <div className="adm-add">
              <input
                className="adm-input adm-input-grow"
                type="text"
                placeholder="ID, @username или имя"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
              />
              <button type="button" className="adm-btn adm-btn-primary" disabled={searching} onClick={() => runSearch()}>
                Найти
              </button>
            </div>
            <div className="adm-sorts">
              {[
                { k: 'all', t: 'Все' },
                { k: 'active', t: 'С подпиской' },
                { k: 'expired', t: 'Истёкшие' },
              ].map((s) => (
                <button
                  key={s.k}
                  type="button"
                  className={`adm-chip ${searchStatus === s.k ? 'active' : ''}`}
                  onClick={() => { setSearchStatus(s.k); runSearch(s.k); }}
                >
                  {s.t}
                </button>
              ))}
            </div>

            {!card && (
              <div className="adm-admins">
                {searching ? (
                  <div className="adm-center">Поиск…</div>
                ) : searchResults.length === 0 ? (
                  <div className="adm-center adm-subtle">Введите запрос и нажмите «Найти»</div>
                ) : (
                  searchResults.map((u) => (
                    <button key={u.userid} type="button" className="adm-admin-row adm-row-btn" onClick={() => openCard(u.userid)}>
                      <div className="adm-admin-info">
                        <div className="adm-name">{u.first_name || '—'}{u.username ? ` · @${u.username}` : ''}</div>
                        <div className="adm-subtle">{u.userid} · {u.subscription_plan || 'без подписки'}{u.subscription_end_at ? ` до ${fmtDate(u.subscription_end_at)}` : ''}</div>
                      </div>
                      <span className="adm-subtle">›</span>
                    </button>
                  ))
                )}
              </div>
            )}

            {card && (
              <div className="adm-cardview">
                <button type="button" className="adm-btn" onClick={() => setCard(null)}>← К результатам</button>
                <div className="adm-name adm-cardname">
                  {card.first_name || '—'}{card.username ? ` · @${card.username}` : ''}
                </div>
                <div className="adm-kv">
                  <div><span className="adm-subtle">ID</span><b className="adm-mono">{card.userid}</b></div>
                  <div><span className="adm-subtle">Тариф</span><b>{card.subscription_plan || '—'}</b></div>
                  <div><span className="adm-subtle">Подписка до</span><b>{fmtDate(card.subscription_end_at)}</b></div>
                  <div><span className="adm-subtle">Регистрация</span><b>{fmtDate(card.registered_at)}</b></div>
                  <div><span className="adm-subtle">Платежей</span><b>{card.payments_count} на {Math.round(card.payments_sum)} ₽</b></div>
                  <div><span className="adm-subtle">Дней с VPN</span><b>{card.vpn_active_days}</b></div>
                  <div><span className="adm-subtle">Автоплатёж</span><b>{card.autopay_enabled ? (card.autopay_method_title || 'вкл') : 'выкл'}</b></div>
                  <div><span className="adm-subtle">Метка</span><b>{card.label || '—'}</b></div>
                </div>

                {card.last_payments?.length > 0 && (
                  <div className="adm-table-wrap">
                    <table className="adm-table">
                      <thead><tr><th>Дата</th><th>Сумма</th><th>Статус</th></tr></thead>
                      <tbody>
                        {card.last_payments.map((p, i) => (
                          <tr key={i}>
                            <td className="adm-nowrap">{fmtDate(p.created_at)}</td>
                            <td>{Math.round(p.amount)} ₽</td>
                            <td className="adm-subtle">{p.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {tab === 'users' && (
          <div className="adm-card">
            <div className="adm-periods">
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`adm-chip ${period === p.key ? 'active' : ''}`}
                  onClick={() => applyPeriod(p.key)}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {period === 'custom' && (
              <div className="adm-custom">
                <input
                  className="adm-input"
                  type="number"
                  min="1"
                  placeholder="Дней, напр. 45"
                  value={customDays}
                  onChange={(e) => { setCustomDays(e.target.value); setDateFrom(''); setDateTo(''); }}
                />
                <span className="adm-or">или</span>
                <input className="adm-input" type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setCustomDays(''); }} />
                <input className="adm-input" type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setCustomDays(''); }} />
                <button type="button" className="adm-btn adm-btn-primary" onClick={() => { setPage(0); loadStats(); if (tab === 'users') loadRows(); }}>
                  Применить
                </button>
              </div>
            )}
          </div>
        )}

        {tab === 'stats' && (
          <>
            <div className="adm-stats-heading">
              <div>
                <div className="adm-eyebrow">ОПЕРАЦИОННАЯ АНАЛИТИКА</div>
                <h1>Состояние сервиса</h1>
                <p>Фактические записи из PostgreSQL и журнала VPN-активности</p>
              </div>
              <div className="adm-heading-status"><span className="adm-live-dot" /> Данные загружены</div>
            </div>

            <div className="adm-toolbar">
              <div className="adm-periods">
                {PERIODS.map((p) => (
                  <button key={p.key} type="button" className={`adm-chip ${period === p.key ? 'active' : ''}`} onClick={() => applyPeriod(p.key)}>{p.label}</button>
                ))}
              </div>
              <div className="adm-toolbar-actions">
                <label className="adm-compare"><input type="checkbox" checked={compare} onChange={(e) => setCompare(e.target.checked)} /> Сравнить с предыдущим периодом</label>
                <button type="button" className="adm-btn" disabled={loadingStats} onClick={loadStats}>{loadingStats ? 'Обновление…' : 'Обновить'}</button>
              </div>
              {period === 'custom' && (
                <div className="adm-custom adm-custom-wide">
                  <input className="adm-input" type="number" min="1" placeholder="Количество дней" value={customDays} onChange={(e) => { setCustomDays(e.target.value); setDateFrom(''); setDateTo(''); }} />
                  <span className="adm-or">или</span>
                  <input className="adm-input" type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setCustomDays(''); }} />
                  <input className="adm-input" type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setCustomDays(''); }} />
                  <button type="button" className="adm-btn adm-btn-primary" onClick={loadStats}>Применить</button>
                </div>
              )}
            </div>

            <div className="adm-proof-strip">
              <div><span>Период</span><b>{summary?.date_from || '—'} — {summary?.date_to || '—'}</b></div>
              <div><span>Часовой пояс</span><b>Москва (UTC+3)</b></div>
              <div><span>Срез</span><b>Только завершённые дни</b></div>
              <div><span>Обновлено</span><b>{statsLoadedAt ? statsLoadedAt.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '—'}</b></div>
            </div>

            <div className="adm-metrics">
              {KPI_CARDS.map(({ key, label, hint, group, explanation }) => {
                const change = summary?.deltas_percent?.[key];
                return (
                  <div className="adm-metric" key={key} title={explanation}>
                    <div className="adm-metric-group">{group}</div>
                    <div className="adm-metric-label">{label}</div>
                    <div className="adm-metric-value">{formatKpi(key, summary?.totals?.[key])}</div>
                    <div className="adm-metric-foot">
                      <span>{hint}</span>
                      {compare && change != null && <b className="adm-delta">{formatDelta(change)}</b>}
                    </div>
                    <div className="adm-metric-definition">{explanation}</div>
                  </div>
                );
              })}
            </div>

            <div className="adm-card adm-chart-card">
              <div className="adm-section-head"><div><h2>Динамика по дням</h2><p>Каждая точка — запись за конкретный завершённый день</p></div><span>до 3 рядов</span></div>
              <div className="adm-metric-picker">
                {CHART_METRICS.map(([key, label, color]) => (
                  <button key={key} type="button" className={`adm-chart-chip ${chartMetrics.includes(key) ? 'active' : ''}`} onClick={() => toggleChartMetric(key)}>
                    <i style={{ background: color }} />{label}
                  </button>
                ))}
              </div>
              {loadingStats ? (
                <div className="adm-chart-empty">Загрузка…</div>
              ) : (
                <Chart points={summary?.points || []} metrics={chartMetrics} />
              )}
              <div className="adm-hint">
                Левая шкала — пользователи и события. Для выручки включается отдельная правая шкала в рублях.
                {!summary?.data_quality?.active_subscription_history ? ' Метрики paid-подписок скрыты до накопления дневной истории.' : ''}
              </div>
            </div>

            <div className="adm-card adm-audit-card">
              <div className="adm-section-head"><div><h2>Проверка цифр</h2><p>Те же значения без визуализации — по одной строке на день</p></div><div className="adm-actions"><button type="button" className="adm-btn" onClick={exportDailyCsv}>Скачать метрики CSV</button><button type="button" className="adm-btn" onClick={exportAuditCsv}>Скачать аудит пользователей</button></div></div>
              <div className="adm-table-wrap">
                <table className="adm-table adm-analytics-table">
                  <thead><tr><th>Дата</th><th>Регистрации</th><th>Выдано ключей</th><th>Первый VPN</th><th>VPN за день</th><th>Оплаченные подписки</th><th>Платящие с VPN</th><th>Первые оплаты</th><th>Повторные оплаты</th><th>Получено</th></tr></thead>
                  <tbody>{(summary?.points || []).map((p) => <tr key={p.date}>
                    <td className="adm-nowrap">{shortDay(p.date)}</td><td>{p.new_users}</td><td>{p.vpn_keys_issued}</td>
                    <td>{p.first_vpn_users ?? '—'}</td><td>{p.vpn_dau ?? '—'}</td><td>{p.active_paid_subscriptions ?? '—'}</td>
                    <td>{p.active_paid_vpn_7d ?? '—'}</td><td>{p.first_payment_users}</td><td>{p.renewal_users}</td><td className="adm-nowrap">{fmtRub(p.revenue_rub)}</td>
                  </tr>)}</tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {tab === 'users' && (
          <div className="adm-card">
            <div className="adm-rowhead">
              <span>Найдено: {total}</span>
              <span className="adm-subtle">
                {summary ? `${summary.date_from} — ${summary.date_to}` : ''}
              </span>
            </div>
            <div className="adm-sorts">
              {[["all","Все"],["trial","Trial"],["paid","Платящие"],["expired","Истёкшие"],["active","Активные VPN"],["never","Не использовали VPN"]].map(([k,t]) => (
                <button key={k} type="button" className={`adm-chip ${userFilter === k ? 'active' : ''}`} onClick={() => { setPage(0); setUserFilter(k); }}>{t}</button>
              ))}
            </div>
            <div className="adm-sorts">
              {SORTS.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  className={`adm-chip ${sort === s.key ? 'active' : ''}`}
                  onClick={() => toggleSort(s.key)}
                >
                  {s.label}{sort === s.key ? (order === 'desc' ? ' ↓' : ' ↑') : ''}
                </button>
              ))}
            </div>

            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Пользователь</th>
                    <th>ID</th>
                    <th>Источник</th><th>Подписка</th><th>Первый VPN</th><th>Последний VPN</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingRows ? (
                    <tr><td colSpan={8} className="adm-center">Загрузка…</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={8} className="adm-center">Никого не найдено</td></tr>
                  ) : (
                    rows.map((r) => (
                      <tr key={r.userid}>
                        <td className="adm-nowrap">{fmtDate(r.registered_at)}</td>
                        <td>
                          <div className="adm-name">{r.first_name || '—'}</div>
                          {r.username ? <div className="adm-subtle">@{r.username}</div> : null}
                        </td>
                        <td className="adm-mono">{r.userid}</td>
                        <td>{r.source_first || 'organic'}</td>
                        <td>
                          {r.subscription_plan || '—'}
                          {r.subscription_status ? <div className="adm-subtle">{r.subscription_status}</div> : null}
                        </td>
                        <td>{r.first_vpn_at || '—'}</td><td>{r.last_vpn_at || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {total > PAGE_SIZE && (
              <div className="adm-pager">
                <button type="button" className="adm-btn" disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>← Назад</button>
                <span className="adm-subtle">Стр. {page + 1} из {totalPages}</span>
                <button type="button" className="adm-btn" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Вперёд →</button>
              </div>
            )}
          </div>
        )}

        {tab === 'funnels' && (
          <div className="adm-card">
            <div className="adm-sorts">
              {[
                { k: 2, t: 'Онбординг' },
                { k: 3, t: 'Установка' },
                { k: 1, t: 'Покупка' },
                { k: 4, t: 'Рефералка' },
              ].map((f) => (
                <button key={f.k} type="button" className={`adm-chip ${funnelId === f.k ? 'active' : ''}`} onClick={() => { triggerHaptic(); setFunnelId(f.k); }}>
                  {f.t}
                </button>
              ))}
            </div>
            <div className="adm-sorts">
              {[7, 30, 90, 365].map((d) => (
                <button key={d} type="button" className={`adm-chip ${funnelDays === d ? 'active' : ''}`} onClick={() => { triggerHaptic(); setFunnelDays(d); }}>
                  {d} дн.
                </button>
              ))}
            </div>
            {funnelLoading ? (
              <div className="adm-center">Загрузка…</div>
            ) : funnel ? (
              <>
                <div className="adm-name adm-cardname">{funnel.name}</div>
                <div className="adm-funnel">
                  {funnel.steps.map((s, i) => {
                    const maxU = funnel.steps[0]?.unique || 1;
                    const w = Math.max(4, Math.round((s.unique / (maxU || 1)) * 100));
                    return (
                      <div key={i} className="adm-fstep">
                        <div className="adm-fstep-head">
                          <span>{i + 1}. {s.label}</span>
                          <b>{s.unique}{s.conv_pct != null ? <span className="adm-subtle"> · {s.conv_pct}%</span> : null}</b>
                        </div>
                        <div className="adm-fbar"><div className="adm-fbar-fill" style={{ width: `${w}%` }} /></div>
                        <div className="adm-subtle">{s.total} событий</div>
                      </div>
                    );
                  })}
                </div>
                {funnel.extra?.payments_succeeded != null && (
                  <div className="adm-hint">✅ Успешных оплат за период: <b>{funnel.extra.payments_succeeded}</b></div>
                )}
                {funnel.extra?.unplugged_count != null && (
                  <div className="adm-hint">⚠️ Купили, но не подключили ключ: <b>{funnel.extra.unplugged_count}</b> чел.</div>
                )}
              </>
            ) : (
              <div className="adm-center adm-subtle">Нет данных</div>
            )}
          </div>
        )}

        {tab === 'ads' && (
          <div className="adm-card">
            <div className="adm-add">
              <input
                className="adm-input adm-input-grow"
                type="text"
                placeholder="Код метки (латиница, цифры, _)"
                value={adCode}
                onChange={(e) => setAdCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') createAd(); }}
              />
              <button type="button" className="adm-btn adm-btn-primary" disabled={serviceBusy || !adCode.trim()} onClick={createAd}>
                Создать
              </button>
            </div>
            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Метка</th>
                    <th>👥 уник.</th>
                    <th>🚀 стартов</th>
                    <th>🎯 действий</th>
                    <th>Ссылка</th>
                  </tr>
                </thead>
                <tbody>
                  {adsLoading ? (
                    <tr><td colSpan={5} className="adm-center">Загрузка…</td></tr>
                  ) : ads.length === 0 ? (
                    <tr><td colSpan={5} className="adm-center">Меток нет</td></tr>
                  ) : (
                    ads.map((a) => (
                      <tr key={a.code}>
                        <td className="adm-name">{a.code}</td>
                        <td>{a.start_users}</td>
                        <td>{a.start_events}</td>
                        <td>{a.interaction_users}</td>
                        <td>
                          <button type="button" className="adm-btn adm-btn-mini" onClick={() => copyText(a.link)}>Копировать</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="adm-hint">👥 уникальных по ссылке · 🚀 всего переходов · 🎯 уникальных с действиями. Ссылка вида t.me/бот?start=ad_код.</div>
          </div>
        )}

        {tab === 'admins' && (
          <div className="adm-card">
            <div className="adm-add">
              <input
                className="adm-input adm-input-grow"
                type="number"
                placeholder="Telegram ID нового админа"
                value={newAdminId}
                onChange={(e) => setNewAdminId(e.target.value)}
              />
              <button type="button" className="adm-btn adm-btn-primary" disabled={adminBusy} onClick={handleAddAdmin}>
                Добавить
              </button>
            </div>

            <div className="adm-admins">
              {admins.length === 0 ? (
                <div className="adm-center adm-subtle">Список пуст</div>
              ) : (
                admins.map((a) => (
                  <div key={a.userid} className="adm-admin-row">
                    <div className="adm-admin-info">
                      <div className="adm-name">
                        {a.first_name || 'Админ'}
                        {String(a.userid) === String(myId) ? <span className="adm-you"> — это вы</span> : null}
                      </div>
                      <div className="adm-subtle">
                        {a.username ? `@${a.username} · ` : ''}{a.userid}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="adm-btn adm-btn-danger"
                      disabled={adminBusy || String(a.userid) === String(myId)}
                      onClick={() => handleRemoveAdmin(a.userid)}
                    >
                      Удалить
                    </button>
                  </div>
                ))
              )}
            </div>
            <div className="adm-hint">
              Права действуют и на сайте, и в Telegram-боте — список синхронизируется автоматически.
            </div>

          </div>
        )}

        <button type="button" className="adm-back" onClick={() => { triggerHaptic(); onBack?.(); }}>
          Назад в профиль
        </button>
      </div>
      <nav className="adm-mobile-nav" aria-label="Разделы админ-панели">
        {ADMIN_NAV.map((x) => <button key={x.k} type="button" className={tab === x.k ? 'active' : ''} onClick={() => { triggerHaptic(); setTab(x.k); }}>{x.t}</button>)}
      </nav>
      </div>
    </div>
  );
}

export default AdminPage;
